import { Injectable } from '@angular/core';
import { ApiClientHelper } from '../../helpers/api-client.helper';
import { AppConfig } from '../../../shared/config/app-config';
import { LocalStorageService } from '../../services/local-storage.service';
import { AuthenticationService } from '../../services/authentication.service';
import { MobileWebViewService } from '../../services/mobile-web-view.service';
import { StorageKeys } from '../../../shared/models/storage-keys';
import { StringHelper } from '../../helpers/strings.helper';
import {
    Pageable,
    RepaymentScheduleInfo,
    ParticipantCashout,
    IncomeCommissionsTransactionsParams,
    PayoutTransactionsHistoryParams,
    LoanRequestListParams,
    PortfolioListParams,
    PrequalificationListParams,
    PageParams,
    PortfolioManagementServiceError,
} from '../wcl-portfolio-management.model';
import { LoanOfferCreationPayload } from '../prequalification-details.types';
import {
    PrequalificationRecordResponse,
    BusinessTypeResponse,
    LoanOfferGenerationResponse,
    RepaymentScheduleGenerationResponse,
} from '../wcl-portfolio-management.model';
import moment from 'moment';
import {
    LoanTenureOptionsRequestParams,
    EligibleAmountRequestCreationParams,
    EligibleAmountRequestOfferRepaymentScheduleGenerationParams,
    RepaymentFrequencyOptionsRequestParams,
    EligibleAmountRequestCreationParamsV2,
} from '../prequalification-lookup.models';
import { LoanScheduleSimulationRequestParams } from '../loan-schedule-simulation.models';

@Injectable({
    providedIn: 'root',
})
export class WclPortfolioManagementService {
    customHeaders: any = {};
    userRole: any;

    constructor(
        private apiClientHelper: ApiClientHelper,
        public config: AppConfig,
        private authService: AuthenticationService,
        private mobileService: MobileWebViewService,
        public localStorage: LocalStorageService,
    ) {
        this.customHeaders = {};
        this.userRole = this.localStorage.get(StorageKeys.USER_ROLE);
    }

    // SERVICE UTILS
    getDaysLeft(targetDate: Date) {
        const presentDate = new Date();
        const differenceInTime = targetDate?.getTime() - presentDate?.getTime();
        const differenceInDays = Math.ceil(differenceInTime / (1000 * 3600 * 24));

        if (differenceInDays > 0) {
            return `${differenceInDays} days left`;
        } else if (differenceInDays < 0) {
            return `${Math.abs(differenceInDays)} days ago`;
        } else {
            return 'Today!';
        }
    }

    betterFormattedDaysMessage(targetDate: Date, completedDate: Date) {
        const targetDate_ = moment(targetDate);
        const seconds = targetDate_.diff(completedDate, 'second');
        const minutes = targetDate_.diff(completedDate, 'minute');
        const hours = targetDate_.diff(completedDate, 'hour');
        const days = targetDate_.diff(completedDate, 'day');

        if (days > 0) {
            return `in the next ${days} days`;
        } else if (days < 0) {
            return `${Math.abs(days)} days late`;
        } else if (days === 0) {
            return 'Today!';
        } else if (hours > 0) {
            return `in the next ${hours} hours`;
        } else if (minutes > 0) {
            return `in the next ${minutes} minutes`;
        } else {
            return `in the next ${seconds} seconds`;
        }
    }

    // ---------------------
    // BRM SC RM LOAN REQUEST AND PORTFOLIO MANAGEMENT ENDPOINTS
    // ---------------------

    getPortfolioList(params: Pageable<PortfolioListParams>) {
        const paramString = StringHelper.toUrlString(params);
        const url = this.buildMPMSRequestUrl(`/api/v1/contracts?${paramString}`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getPortfolioListCount() {
        const url = this.buildMPMSRequestUrl(`/api/v1/contracts`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getLoanRequestList(params: Pageable<LoanRequestListParams>) {
        const paramString = StringHelper.toUrlString(params);
        const url = this.buildMPMSRequestUrl(`/api/v1/loan-requests?${paramString}`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getLoanRequestsListCount() {
        const url = this.buildMPMSRequestUrl(`/api/v1/loan-requests/summary`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getSingleLoanRequestData(loanReference: string) {
        loanReference = encodeURIComponent(loanReference);
        const url = this.buildMPMSRequestUrl(`/api/v1/loan-requests/${loanReference}`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getImageRef(imageRef: string) {
        const url = this.buildMPMSRequestUrl(`/api/v1/file/image/${imageRef}`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getBRMsLinkedToSC() {
        const url = this.buildMPMSRequestUrl(`/api/v1/sc/brm`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getSCsLinkedToRM() {
        const url = this.buildMPMSRequestUrl(`/api/v1/rm/sc`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getBRMsLinkedToRMs(code: string) {
        code = encodeURIComponent(code);
        const url = this.buildMPMSRequestUrl(`/api/v1/rm/sc/${code}/brm`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getParticipantCommissions() {
        const url = this.buildMPMSRequestUrl(`/api/v1/commission`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getParticipantCommissionsTransactions(params: Pageable<IncomeCommissionsTransactionsParams>) {
        const paramString = StringHelper.toUrlString(params);
        const url = this.buildMPMSRequestUrl(`/api/v1/commission/transaction?${paramString}`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getParticipantPayoutTransactionHistory(params: Pageable<PayoutTransactionsHistoryParams>) {
        const paramString = StringHelper.toUrlString(params);
        const url = this.buildMPMSRequestUrl(`/api/v1/commission/payout/transaction/history?${paramString}`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getParticipantPayoutCashoutBalance() {
        const url = this.buildMPMSRequestUrl(`/api/v1/commission/payout/balance`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getParticipantCashoutAccountNumber() {
        const url = this.buildMPMSRequestUrl(`/api/v1/commission/cashout`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    participantCashoutAction(data: ParticipantCashout) {
        const url = this.buildMPMSRequestUrl(`/api/v1/commission/cashout`);
        return this.apiClientHelper.postForPromise(url, data, true, false, false, 'WEB', this.customHeaders);
    }

    // ---------------------
    // endpoints on portfolio management details
    // ---------------------

    async getWclLoanDetailsReq(params: { contractRef: string }) {
        const contractRef = encodeURIComponent(params.contractRef);
        const url = this.buildMPMSRequestUrl(`/api/v1/contracts/${contractRef}`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    async getOverdraftLoanDetailsReq(loanContractRef: string) {
        const contractRef = encodeURIComponent(loanContractRef);
        const url = this.buildMPMSRequestUrl(`/api/v1/contracts/overdraft/${contractRef}`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getRepaymentSchedule(params: PageParams, contractRef: string) {
        const paramString = StringHelper.toUrlString(params);
        const url = this.buildMPMSRequestUrl(`/api/v1/contracts/${contractRef}/repayment-schedule?${paramString}`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getRepaymentHistory(params: PageParams, contractRef: string) {
        const paramString = StringHelper.toUrlString(params);
        const url = this.buildMPMSRequestUrl(`/api/v1/contracts/${contractRef}/repayment-history?${paramString}`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getInterestAccrued(params: PageParams, contractRef: string) {
        const paramString = StringHelper.toUrlString(params);
        const url = this.buildMPMSRequestUrl(`/api/v1/contracts/${contractRef}/accrued-interest-history?${paramString}`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getLoanParticipants(params: PageParams, loanContractRef: string) {
        const contractRef = encodeURIComponent(loanContractRef);
        const paramString = StringHelper.toUrlString(params);
        const url = this.buildMPMSRequestUrl(`/api/v1/contracts/${contractRef}/participants?${paramString}`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getGuarantors(params: { requestRef: string }) {
        const url = this.buildMPMSRequestUrl(`/api/v1/loan-requests/${params.requestRef}/guarantors`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getImageUrlFromRef(imageRef: string) {
        const url = this.buildMPMSRequestUrl(`/api/v1/file/image/${imageRef}`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    // ---------------------
    // currently un-used
    // ---------------------

    getMissedRepayments(params, loanContractRef: string) {
        const contractRef = encodeURIComponent(loanContractRef);
        const url = this.buildMPMSRequestUrl(`/api/v1/wcl/loans/${contractRef}/missed-repayments`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders, params);
    }

    getLegalInfo(loanContractRef: string) {
        const contractRef = encodeURIComponent(loanContractRef);
        const url = this.buildMPMSRequestUrl(`/api/v1/wcl/loans/${contractRef}/legal-info`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getLoanParticipant(loanContractRef: string, id: string) {
        const url = this.buildMPMSRequestUrl(`/api/v1/wcl/loans/${loanContractRef}/participants/${id}`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getGuarantorLegalInfoData(loanContractRef: string, guarantorId: string) {
        const url = this.buildMPMSRequestUrl(`/api/v1/wcl/loans/${loanContractRef}/guarantors/${guarantorId}/legal-info`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getFinanceInfo(loanContractRef: string) {
        const url = this.buildMPMSRequestUrl(`/api/v1/wcl/loans/${loanContractRef}/finance-info`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getSmartTurnoverMetrics(loanContractRef: string) {
        const url = this.buildMPMSRequestUrl(`/api/v1/wcl/loans/${loanContractRef}/smart-turnover/metrics`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getSmartTurnoverTrend(loanContractRef: string) {
        const url = this.buildMPMSRequestUrl(`/api/v1/wcl/loans/${loanContractRef}/smart-turnover/trend`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    // ---------------------
    // endpoints in progress.. currently un-finished
    // ---------------------

    createLoanScheduleSimulation(data: LoanScheduleSimulationRequestParams) {
        const url = this.buildMPMSRequestUrl(`/api/v1/simulated-loan-schedule`);
        return this.apiClientHelper.postForPromise(url, data, true, false, false, 'WEB', this.customHeaders);
    }

    createEligibleAmountRequest(data: EligibleAmountRequestCreationParams) {
        const url = this.buildMPMSRequestUrl(`/api/v1/business-owner/eligible-amount`);
        return this.apiClientHelper.postForPromise(url, data, true, false, false, 'WEB', this.customHeaders);
    }

    createEligibleAmountRequestV2(data: EligibleAmountRequestCreationParamsV2) {
        const url = this.buildMPMSRequestUrl(`/api/v2/business-owner/eligible-amount`);
        return this.apiClientHelper.postForPromise(url, data, true, false, false, 'WEB', this.customHeaders);
    }

    getSimulatedRepaymentScheduleForEligibleAmountRequest(eligibleAmountRef: string) {
        const encodedRef = encodeURIComponent(eligibleAmountRef);
        const url = this.buildMPMSRequestUrl(`/api/v1/business-owner/eligible-amount/${encodedRef}/simulated-schedule`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getSimulatedRepaymentScheduleForEligibleAmountRequestOffer({
        eligibleAmountReference, offerReference, ...params
    }: EligibleAmountRequestOfferRepaymentScheduleGenerationParams) {
        const encodedAmountRef = encodeURIComponent(eligibleAmountReference);
        const encodedOfferRef = encodeURIComponent(offerReference);
        const paramsString = StringHelper.toUrlString(params);
        const url = this.buildMPMSRequestUrl(
            `/api/v2/business-owner/eligible-amount/${encodedAmountRef}/offers/${encodedOfferRef}/simulated-repayment-schedule?${paramsString}`
        );
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getLoanTenureOptions(params: LoanTenureOptionsRequestParams) {
        const paramsString = StringHelper.toUrlString(params);
        const url = this.buildMPMSRequestUrl(`/api/v2/eligible-amount/tenure-options?${paramsString}`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getRepaymentFrequencyOptions(params: RepaymentFrequencyOptionsRequestParams) {
        const paramsString = StringHelper.toUrlString(params);
        const url = this.buildMPMSRequestUrl(`/api/v2/eligible-amount/repayment-frequency-options?${paramsString}`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getPrequalificationLookup(accountRef: string) {
        const encodedRef = encodeURIComponent(accountRef);
        const url = this.buildMPMSRequestUrl(`/api/v1/business-owner/details/account-nuban/${encodedRef}`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getPrequalificationLookupV2(accountRef: string) {
        const encodedRef = encodeURIComponent(accountRef);
        const url = this.buildMPMSRequestUrl(`/api/v2/business-owner/details/account-nuban/${encodedRef}`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getPrequalificationLookupDetails(requestRef: string) {
        const encodedRef = encodeURIComponent(requestRef);
        const url = this.buildMPMSRequestUrl(`/api/v1/business-owner/eligible-amount/${encodedRef}`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getPrequalificationLookupDetailsV2(requestRef: string) {
        const encodedRef = encodeURIComponent(requestRef);
        const url = this.buildMPMSRequestUrl(`/api/v2/business-owner/eligible-amount/${encodedRef}`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getPrequalificationLookupHistory(params: Pageable<{}>) {
        const paramsString = StringHelper.toUrlString(params);
        const url = this.buildMPMSRequestUrl(`/api/v1/business-owner/eligible-amount?${paramsString}`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getPrequalificationLookupHistoryV2(params: Pageable<{}>) {
        const paramsString = StringHelper.toUrlString(params);
        const url = this.buildMPMSRequestUrl(`/api/v2/business-owner/eligible-amount?${paramsString}`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getPrequalificationList(params: Pageable<PrequalificationListParams>) {
        const useV2 = this.config.USE_PREQUALIFIED_BOS_V2;
        const paramsString = StringHelper.toUrlString(params);
        const url = this.buildMPMSRequestUrl(
            useV2 ? `/api/v2/prequalified-business-owners?${paramsString}` : `/api/v1/prequalified-business-owners?${paramsString}`
        );
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    generateRepaymentSchedule1(data: RepaymentScheduleInfo) {
        const url = this.buildMockRequestUrl(`/api/v1/wcl/generate-schedule`);
        return this.apiClientHelper.postForPromise(url, data, true, false, false, 'WEB', this.customHeaders);
    }

    async getPrequalificationRecord(boReference: string) {
        const useV2 = this.config.USE_PREQUALIFIED_BOS_V2;
        boReference = encodeURIComponent(boReference);
        const url = this.buildMPMSRequestUrl(
            useV2 ? `/api/v2/prequalified-business-owners/details/${boReference}`
                : `/api/v1/prequalified-business-owners/details/${boReference}`
        );
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    getBusinessTypes(params: Pageable<{}>): Promise<BusinessTypeResponse> {
        const paramsString = StringHelper.toUrlString(params);
        const url = this.buildMPMSRequestUrl(`/api/v1/business/types?${paramsString}`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    createLoanOffer(data: LoanOfferCreationPayload): Promise<LoanOfferGenerationResponse> {
        const useV2 = this.config.USE_PREQUALIFIED_BOS_V2;
        const url = this.buildMPMSRequestUrl(
            useV2 ? `/api/v2/business-owner/eligible-amount` : `/api/v1/business-owner/eligible-amount`
        );
        return this.apiClientHelper.postForPromise(url, data, true, false, false, 'WEB', this.customHeaders);
    }

    generateRepaymentSchedule(businessOwnerId: string | number): Promise<RepaymentScheduleGenerationResponse> {
        const url = this.buildMPMSRequestUrl(`/api/v1/prequalified-business-owners/details/${businessOwnerId}/simulated-schedule`);
        return this.apiClientHelper.getForPromise(url, true, false, false, this.customHeaders);
    }

    mpmsErrorMessage(error: PortfolioManagementServiceError) {
        return error?.error?.error?.message || StringHelper.getErrorMessageHttpErrorResponse(error);
    }

    buildMPMSRequestUrl(path) {
        const url = this.config.WCL_PORTFOLIO_MANAGEMENT + path;
        return url;
    }
    buildMockRequestUrl(path) {
        const url = mockUrl + path;
        return url;
    }
}

const mockUrl = 'http://localhost:3001';
